import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "panel", "searchForm", "searchInput", "searchContext", "filterContext", "filterButton"]
  static values = {
    autoSwitchDiscover: Boolean
  }

  connect() {
    // Set initial tab state
    this.currentTab = this.autoSwitchDiscoverValue ? 'discover' : 'your-squads'
    
    // Update search field based on current tab
    this.updateSearchField()
    
    // Update filter button visibility
    this.updateFilterButtonVisibility()
    
    // Check if we should auto-switch to discover tab
    if (this.autoSwitchDiscoverValue) {
      // Show discover (index 1), hide your-squads (index 0)
      this.panelTargets.forEach((panel, index) => {
        panel.style.display = index === 1 ? 'block' : 'none'
      })
      
      // Update tab buttons to match
      const discoverTab = this.tabTargets.find(tab => tab.dataset.tab === 'discover')
      if (discoverTab) {
        this.updateTabStyling(discoverTab)
      }
    } else {
      // Normal: show first panel, hide others
      this.panelTargets.forEach((panel, index) => {
        panel.style.display = index === 0 ? 'block' : 'none'
      })
    }
  }

  updateTabStyling(selectedTab) {
    // Update tab button styling without activating panels
    this.tabTargets.forEach(tab => {
      tab.classList.remove('bg-primary', 'text-white')
      tab.classList.add('bg-white', 'text-text-secondary', 'border', 'border-border')
    })
    
    selectedTab.classList.remove('bg-white', 'text-text-secondary', 'border', 'border-border')
    selectedTab.classList.add('bg-primary', 'text-white')
  }

  selectTab(event) {
    event.preventDefault()
    const selectedTab = event.currentTarget
    this.currentTab = selectedTab.dataset.tab
    
    // Update search field to show this tab's search
    this.updateSearchField()
    
    // Update filter button visibility
    this.updateFilterButtonVisibility()
    
    this.activateTab(selectedTab)
  }

  switchToDiscover(event) {
    event.preventDefault()
    const discoverTab = this.tabTargets.find(tab => tab.dataset.tab === 'discover')
    if (discoverTab) {
      this.currentTab = 'discover'
      this.updateSearchField()
      this.updateFilterButtonVisibility()
      this.activateTab(discoverTab)
    }
  }
  
  updateFilterButtonVisibility() {
    if (!this.hasFilterButtonTarget) return
    
    // Show filter button only on Discover tab
    if (this.currentTab === 'discover') {
      this.filterButtonTarget.classList.remove('hidden')
    } else {
      this.filterButtonTarget.classList.add('hidden')
    }
  }

  activateTab(selectedTab) {
    const tabId = selectedTab.dataset.tab

    // Update tab styling - deactivate all tabs first
    this.tabTargets.forEach(tab => {
      tab.classList.remove('bg-primary', 'text-white', 'hover:bg-primary-hover')
      tab.classList.add('bg-white', 'text-text-secondary', 'border', 'border-border', 'hover:border-primary', 'hover:text-primary')
    })
    
    // Activate the selected tab
    selectedTab.classList.remove('bg-white', 'text-text-secondary', 'border', 'border-border', 'hover:border-primary', 'hover:text-primary')
    selectedTab.classList.add('bg-primary', 'text-white', 'hover:bg-primary-hover')
    
    // Show/hide panels using inline styles instead of Tailwind classes
    this.panelTargets.forEach(panel => {
      panel.style.display = panel.dataset.tabName === tabId ? 'block' : 'none'
    })
    
    // Update filter form values for the new tab
    this.updateFilterValues()
  }

  updateSearchField() {
    if (!this.hasSearchInputTarget) return
    
    // Get the stored search values for each tab
    const yourSquadsValue = this.searchInputTarget.dataset.yourSquadsValue || ""
    const discoverValue = this.searchInputTarget.dataset.discoverValue || ""
    
    // Update search input based on current tab
    if (this.currentTab === 'your-squads') {
      this.searchInputTarget.value = yourSquadsValue
      this.searchInputTarget.placeholder = "Search your squads..."
      if (this.hasSearchContextTarget) {
        this.searchContextTarget.value = 'your_squads'
      }
    } else {
      this.searchInputTarget.value = discoverValue
      this.searchInputTarget.placeholder = "Search all squads..."
      if (this.hasSearchContextTarget) {
        this.searchContextTarget.value = 'discover'
      }
    }
  }

  handleSearchSubmit(event) {
    // Rename the 'q' field to the tab-specific param name before submission
    const form = event.target
    const qInput = form.querySelector('input[name="q"]')
    const contextInput = form.querySelector('input[name="search_context"]')
    
    if (qInput && qInput.value && contextInput) {
      const context = contextInput.value
      
      // Rename to tab-specific param
      if (context === 'your_squads') {
        qInput.name = 'your_squads_q'
      } else {
        qInput.name = 'discover_q'
      }
    }
    
    // Form will submit normally with the renamed param
  }

  clearSearch(event) {
    event.preventDefault()
    if (!this.hasSearchInputTarget) return
    
    // Build URL without current tab's search param
    const url = new URL(window.location)
    
    if (this.currentTab === 'your-squads') {
      url.searchParams.delete('your_squads_q')
    } else {
      url.searchParams.delete('discover_q')
    }
    
    // Navigate to URL without search
    window.location.href = url.toString()
  }
  
  handleFilterSubmit(event) {
    event.preventDefault()
    
    const form = event.target
    const filterContext = this.hasFilterContextTarget ? this.filterContextTarget.value : 'discover'
    const formData = new FormData(form)
    
    // Build new URL with tab-prefixed filter params
    const params = new URLSearchParams()
    
    // Preserve search params
    if (formData.get('your_squads_q')) {
      params.append('your_squads_q', formData.get('your_squads_q'))
    }
    if (formData.get('discover_q')) {
      params.append('discover_q', formData.get('discover_q'))
    }
    
    // Add tab-prefixed filter params
    const prefix = filterContext === 'your_squads' ? 'your_squads_' : 'discover_'
    
    if (formData.get('focus')) {
      params.append(prefix + 'focus', formData.get('focus'))
    }
    if (formData.get('stage')) {
      params.append(prefix + 'stage', formData.get('stage'))
    }
    if (formData.get('privacy')) {
      params.append(prefix + 'privacy', formData.get('privacy'))
    }
    if (formData.get('has_seats') === '1') {
      params.append(prefix + 'has_seats', '1')
    }
    
    // Close modal and navigate
    if (typeof window.hideFilterModal === 'function') {
      window.hideFilterModal()
    }
    window.location.href = `${window.location.pathname}?${params.toString()}`
  }
  
  updateFilterValues() {
    if (!this.hasFilterContextTarget) return
    
    const filterForm = document.querySelector('#filter-modal form')
    if (!filterForm) return
    
    const isYourSquads = this.currentTab === 'your-squads'
    const prefix = isYourSquads ? 'your_squads_' : 'discover_'
    
    // Get URL params
    const urlParams = new URLSearchParams(window.location.search)
    
    // Update filter context
    this.filterContextTarget.value = isYourSquads ? 'your_squads' : 'discover'
    
    // Update select fields
    const focusSelect = filterForm.querySelector('select[name="focus"]')
    if (focusSelect) {
      focusSelect.value = urlParams.get(prefix + 'focus') || ''
    }
    
    const stageSelect = filterForm.querySelector('select[name="stage"]')
    if (stageSelect) {
      stageSelect.value = urlParams.get(prefix + 'stage') || ''
    }
    
    const privacySelect = filterForm.querySelector('select[name="privacy"]')
    if (privacySelect) {
      privacySelect.value = urlParams.get(prefix + 'privacy') || ''
    }
    
    const hasSeatsCheckbox = filterForm.querySelector('input[name="has_seats"]')
    if (hasSeatsCheckbox) {
      hasSeatsCheckbox.checked = urlParams.get(prefix + 'has_seats') === '1'
    }
  }
}
